import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { QRCodeSVG } from 'qrcode.react';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiUsers, FiMapPin, FiX, FiCopy, FiDownload, FiLink, FiGrid } from 'react-icons/fi';
import TableLayoutEditor from '../../components/TableLayoutEditor';

const DEFAULT_LOCATIONS = ['Indoor', 'Outdoor', 'VIP', 'Private Room', 'Bar Area'];
const ALL_LOCATIONS_VALUE = 'all';

function Tables() {
  const { alert, confirm } = useModal();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'layout'
  const [currentFloor, setCurrentFloor] = useState(1);
  const [locationOptions, setLocationOptions] = useState(DEFAULT_LOCATIONS);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [formData, setFormData] = useState({
    table_number: '',
    capacity: '',
    location: '',
    position_x: 0,
    position_y: 0,
    width: 100,
    height: 100,
    shape: 'rectangle',
    rotation: 0,
    chair_count: '',
    layout_floor: 1
  });
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedTableQR, setSelectedTableQR] = useState(null);
  const [updatingTableStatusIds, setUpdatingTableStatusIds] = useState(new Set());
  const qrRef = useRef(null);

  useEffect(() => {
    fetchTables();
    fetchLocations();
  }, []);

  // Load WhatsApp menu / QR-ordering related config for per-table WhatsApp QR

  useEffect(() => {
    if (!selectedLocation && locationOptions.length > 0) {
      setSelectedLocation(ALL_LOCATIONS_VALUE);
    }
  }, [locationOptions, selectedLocation]);

  useEffect(() => {
    if (!formData.location && selectedLocation && selectedLocation !== ALL_LOCATIONS_VALUE) {
      setFormData((prev) => ({ ...prev, location: selectedLocation }));
    }
  }, [selectedLocation, formData.location]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/tables');
      setTables(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
      await alert('Failed to fetch tables', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/api/settings/restaurant');
      const rawLocations = response.data?.table_locations || '';
      const parsed = rawLocations
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      setLocationOptions(parsed.length > 0 ? parsed : DEFAULT_LOCATIONS);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setLocationOptions(DEFAULT_LOCATIONS);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        capacity: parseInt(formData.capacity),
        chair_count: formData.chair_count ? parseInt(formData.chair_count) : parseInt(formData.capacity),
        layout_floor: parseInt(formData.layout_floor) || 1
      };
      
      if (editingTable) {
        await api.put(`/api/tables/${editingTable.id}`, submitData);
      } else {
        await api.post('/api/tables', submitData);
      }
      fetchTables();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save table:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save table';
      await alert(errorMessage, 'Error', 'error');
    }
  };

  const handleLayoutSave = async (updates) => {
    try {
      console.log('Saving layout updates:', updates);
      const response = await api.patch('/api/tables/layout/bulk', { tables: updates });
      console.log('Layout save response:', response.data);
      await fetchTables();
      await alert('Layout saved successfully!', 'Success', 'success');
    } catch (error) {
      console.error('Failed to save layout:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save layout. Please try again.';
      await alert(`Failed to save layout: ${errorMessage}`, 'Error', 'error');
    }
  };

  const handleTableUpdate = (tableId, changes) => {
    // This is called during drag/resize for immediate visual feedback
    // The actual save happens in handleLayoutSave
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Are you sure you want to delete this table? This action cannot be undone.', 'Delete Table', 'warning');
    if (!confirmed) return;
    
    try {
      await api.delete(`/api/tables/${id}`);
      fetchTables();
    } catch (error) {
      console.error('Failed to delete table:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete table';
      await alert(errorMessage, 'Error', 'error');
    }
  };

  const handleStatusChange = async (tableId, newStatus) => {
    if (!tableId || !newStatus) {
      alert('Invalid table or status');
      return;
    }

    if (updatingTableStatusIds.has(tableId)) {
      return;
    }

    const previousTables = tables;
    setUpdatingTableStatusIds((prev) => new Set(prev).add(tableId));
    setTables((prev) => prev.map((table) => (
      table.id === tableId
        ? { ...table, status: newStatus }
        : table
    )));

    try {
      const response = await api.patch(`/api/tables/${tableId}/status`, { status: newStatus });
      
      if (response.data && response.data.table) {
        // Success - refresh tables list
        fetchTables();
        
        // Show success message if status changed
        if (response.data.code === 'SUCCESS') {
          // Optional: Show a subtle success indicator instead of alert
          console.log(`Table status updated to ${newStatus}`);
        }
      } else {
        // Invalid response structure
        const errorMsg = response.data?.message || 'Invalid response from server';
        setTables(previousTables);
        await alert(errorMsg, 'Error', 'error');
      }
    } catch (error) {
      console.error('Failed to update table status:', error);
      setTables(previousTables);
      
      // Handle different error scenarios
      const status = error.response?.status;
      const errorData = error.response?.data;
      
      // Don't handle 401/403 here - let interceptor handle authentication errors
      if (status === 401 || status === 403) {
        // Authentication/authorization error - interceptor will handle
        // But don't show alert to avoid double messaging
        return;
      }
      
      // Handle validation errors (400)
      if (status === 400) {
        const errorMessage = errorData?.error || 'Invalid request. Please check your input.';
        await alert(errorMessage, 'Error', 'error');
        return;
      }
      
      // Handle not found (404)
      if (status === 404) {
        alert('Table not found. Please refresh the page.');
        fetchTables(); // Refresh to get updated list
        return;
      }
      
      // Handle server errors (500)
      if (status === 500) {
        const errorMessage = errorData?.error || 
                            errorData?.details || 
                            'Server error. Please try again later.';
        await alert(errorMessage, 'Error', 'error');
        return;
      }
      
      // Handle network errors or other issues
      if (!error.response) {
        alert('Network error. Please check your connection and try again.');
        return;
      }
      
      // Generic error message
      const errorMessage = errorData?.error || 
                          errorData?.details || 
                          'Failed to update table status. Please try again.';
      await alert(errorMessage, 'Error', 'error');
    } finally {
      setUpdatingTableStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(tableId);
        return next;
      });
    }
  };

  const filteredTables = tables.filter(table => {
    const matchesSearch = 
      table.table_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || table.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    const normalize = (value) => String(value || '').trim();
    const aVal = normalize(a.table_number);
    const bVal = normalize(b.table_number);

    const aNum = parseInt(aVal, 10);
    const bNum = parseInt(bVal, 10);
    const aIsNum = !Number.isNaN(aNum);
    const bIsNum = !Number.isNaN(bNum);

    if (aIsNum && bIsNum && aNum !== bNum) {
      return aNum - bNum;
    }

    if (aIsNum !== bIsNum) {
      return aIsNum ? -1 : 1;
    }

    return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
  });

  const resetForm = () => {
    setFormData({ 
      table_number: '', 
      capacity: '', 
      location: selectedLocation || locationOptions[0] || '',
      position_x: 0,
      position_y: 0,
      width: 100,
      height: 100,
      shape: 'rectangle',
      rotation: 0,
      chair_count: '',
      layout_floor: currentFloor
    });
    setEditingTable(null);
  };

  const locationTables = useMemo(() => (
    selectedLocation && selectedLocation !== ALL_LOCATIONS_VALUE
      ? tables.filter(t => (t.location || DEFAULT_LOCATIONS[0]) === selectedLocation)
      : tables
  ), [tables, selectedLocation]);

  // Get unique floors for selected location
  const floors = useMemo(
    () => [...new Set(locationTables.map(t => t.layout_floor || 1))].sort((a, b) => a - b),
    [locationTables]
  );

  useEffect(() => {
    if (floors.length > 0 && !floors.includes(currentFloor)) {
      setCurrentFloor(floors[0]);
    }
  }, [floors, currentFloor]);

  if (loading) {
    return <Layout title="Tables"><div>Loading...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Tables Management</h1>
            <p className="text-sm text-gray-600">Manage restaurant tables and seating arrangements</p>
          </div>
          <div className="flex gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded text-sm font-semibold transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List view"
              >
                List
              </button>
              <button
                onClick={() => setViewMode('layout')}
                className={`px-3 py-2 rounded text-sm font-semibold transition-colors ${
                  viewMode === 'layout' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Layout view"
              >
                Layout
              </button>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2 font-medium shadow-sm transition-all"
            >
              <FiPlus className="text-base" /> Add Table
            </button>
          </div>
        </div>

        {/* Layout View */}
        {viewMode === 'layout' && (
          <div>
            <div className="mb-4 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">Location:</span>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                  >
                    <option value={ALL_LOCATIONS_VALUE}>All Locations</option>
                    {locationOptions.map((location) => (
                      <option key={location} value={location}>{location}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {/* Floor Selector */}
            {floors.length > 1 && (
              <div className="mb-4 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">Floor:</span>
                  <div className="flex gap-2 flex-wrap">
                    {floors.map(floor => (
                      <button
                        key={floor}
                        onClick={() => setCurrentFloor(floor)}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                          currentFloor === floor
                            ? 'bg-gray-900 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Floor {floor}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <TableLayoutEditor
                tables={tables}
                onSave={handleLayoutSave}
                onTableUpdate={handleTableUpdate}
                floor={currentFloor}
                locationFilter={selectedLocation === ALL_LOCATIONS_VALUE ? '' : selectedLocation}
              />
            </div>
          </div>
        )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
                <input
                  type="text"
                  placeholder="Search by table number or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
                />
              </div>
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="reserved">Reserved</option>
                <option value="cleaning">Cleaning</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Total Tables</div>
              <div className="text-xl font-bold text-gray-900">{tables.length}</div>
            </div>
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Available</div>
              <div className="text-xl font-bold text-green-600">
                {tables.filter(t => t.status === 'available').length}
              </div>
            </div>
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Occupied</div>
              <div className="text-xl font-bold text-red-600">
                {tables.filter(t => t.status === 'occupied').length}
              </div>
            </div>
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Reserved</div>
              <div className="text-xl font-bold text-yellow-600">
                {tables.filter(t => t.status === 'reserved').length}
              </div>
            </div>
          </div>

          {/* Tables Grid */}
          {filteredTables.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-16 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiGrid className="text-4xl text-gray-400" />
              </div>
              <p className="text-gray-700 text-xl font-semibold mb-2">No tables found</p>
              <p className="text-gray-500 text-sm mb-6">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Add your first table to get started'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <button
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                  className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 flex items-center gap-2 mx-auto font-medium shadow-sm transition-all"
                >
                  <FiPlus /> Add First Table
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredTables.map((table) => (
                <div
                  key={table.id}
                  className={`bg-white rounded-lg border border-gray-200 p-4 shadow-sm transition-all hover:shadow-lg hover:scale-[1.02] duration-200 ${
                    table.status === 'occupied' ? 'border-l-4 border-l-red-500' :
                    table.status === 'reserved' ? 'border-l-4 border-l-yellow-500' :
                    table.status === 'cleaning' ? 'border-l-4 border-l-blue-500' :
                    'border-l-4 border-l-green-500'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Table {table.table_number}</h3>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <FiMapPin className="text-gray-400" />
                        <span className="truncate">{table.location || 'Indoor'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => {
                          setSelectedTableQR(table);
                          setShowQRModal(true);
                        }}
                        className="p-2 bg-gray-50 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all border border-gray-200 hover:border-gray-300 flex items-center justify-center"
                        title="View QR Code & Menu Link"
                      >
                        <img 
                          src="https://img.icons8.com/ios-filled/50/000000/qr-code.png" 
                          alt="QR Code" 
                          className="w-4 h-4 object-contain"
                          onError={(e) => {
                            // Fallback to SVG if image fails
                            e.target.style.display = 'none';
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'block';
                          }}
                        />
                        <svg 
                          className="w-4 h-4 hidden" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="3" width="5" height="5" />
                          <rect x="16" y="3" width="5" height="5" />
                          <rect x="3" y="16" width="5" height="5" />
                          <line x1="9" y1="9" x2="9" y2="9" strokeWidth="2" />
                          <line x1="15" y1="9" x2="15" y2="9" strokeWidth="2" />
                          <line x1="9" y1="15" x2="9" y2="15" strokeWidth="2" />
                          <line x1="15" y1="15" x2="15" y2="15" strokeWidth="2" />
                          <line x1="11" y1="9" x2="13" y2="9" strokeWidth="1.5" />
                          <line x1="11" y1="15" x2="13" y2="15" strokeWidth="1.5" />
                          <line x1="9" y1="11" x2="9" y2="13" strokeWidth="1.5" />
                          <line x1="15" y1="11" x2="15" y2="13" strokeWidth="1.5" />
                        </svg>
                      </button>
                      <select
                        value={table.status}
                        onChange={(e) => handleStatusChange(table.id, e.target.value)}
                        disabled={updatingTableStatusIds.has(table.id)}
                        className={`px-2 py-1 rounded text-xs font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                          table.status === 'occupied' ? 'bg-red-100 text-red-700' :
                          table.status === 'reserved' ? 'bg-yellow-100 text-yellow-700' :
                          table.status === 'cleaning' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        <option value="available">Available</option>
                        <option value="occupied">Occupied</option>
                        <option value="reserved">Reserved</option>
                        <option value="cleaning">Cleaning</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-3">
                    <FiUsers className="text-gray-400" />
                    <span>Capacity: {table.capacity} guests</span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingTable(table);
                        setFormData({
                          table_number: table.table_number,
                          capacity: table.capacity,
                          location: table.location || '',
                          position_x: table.position_x || 0,
                          position_y: table.position_y || 0,
                          width: table.width || 100,
                          height: table.height || 100,
                          shape: table.shape || 'rectangle',
                          rotation: table.rotation || 0,
                          chair_count: table.chair_count || table.capacity,
                          layout_floor: table.layout_floor || 1
                        });
                        setShowModal(true);
                      }}
                      className="flex-1 bg-gray-900 text-white py-2 px-2.5 rounded-lg hover:bg-gray-800 flex items-center justify-center transition-all shadow-sm hover:shadow"
                      title="Edit table"
                    >
                      <FiEdit className="text-sm" />
                    </button>
                    <button
                      onClick={() => handleDelete(table.id)}
                      className="bg-red-50 text-red-700 py-2 px-2.5 rounded-lg hover:bg-red-100 transition-all border border-red-200 shadow-sm hover:shadow"
                      title="Delete table"
                    >
                      <FiTrash2 className="text-sm" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && selectedTableQR && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowQRModal(false);
            setSelectedTableQR(null);
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Table QR Codes</h2>
              <button
                onClick={() => {
                  setShowQRModal(false);
                  setSelectedTableQR(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Table {selectedTableQR.table_number}</p>
              <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block shadow-sm" ref={qrRef}>
                <QRCodeSVG 
                  value={selectedTableQR.menu_url || `${window.location.origin}/menu/${selectedTableQR.id}`}
                  size={200}
                  level="M"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Menu Link</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={selectedTableQR.menu_url || `${window.location.origin}/menu/${selectedTableQR.id}`}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={async () => {
                      const url = selectedTableQR.menu_url || `${window.location.origin}/menu/${selectedTableQR.id}`;
                      navigator.clipboard.writeText(url);
                      await alert('Menu link copied to clipboard!', 'Success', 'success');
                    }}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 transition-all shadow-sm"
                    title="Copy link"
                  >
                    <FiCopy className="text-sm" /> Copy
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const url = selectedTableQR.menu_url || `${window.location.origin}/menu/${selectedTableQR.id}`;
                    window.open(url, '_blank');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 transition-all border border-gray-200"
                >
                  <FiLink className="text-sm" /> Open Menu
                </button>
                <button
                  onClick={() => {
                    // Download QR code as PNG
                    const svg = qrRef.current?.querySelector('svg');
                    if (svg) {
                      const svgData = new XMLSerializer().serializeToString(svg);
                      const canvas = document.createElement('canvas');
                      const ctx = canvas.getContext('2d');
                      const img = new Image();
                      img.onload = () => {
                        canvas.width = img.width;
                        canvas.height = img.height;
                        ctx.drawImage(img, 0, 0);
                        const pngFile = canvas.toDataURL('image/png');
                        const downloadLink = document.createElement('a');
                        downloadLink.download = `table-${selectedTableQR.table_number}-qr.png`;
                        downloadLink.href = pngFile;
                        downloadLink.click();
                      };
                      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 transition-all shadow-sm"
                >
                  <FiDownload className="text-sm" /> Download QR
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowModal(false);
            resetForm();
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingTable ? 'Edit Table' : 'Add New Table'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Table Number *</label>
                <input
                  type="text"
                  value={formData.table_number}
                  onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  required
                  placeholder="e.g., 1, 2, A1, VIP-1"
                />
                <p className="text-xs text-gray-500 mt-1">Must be unique</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Capacity *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  required
                  placeholder="Number of guests"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                <select
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                >
                  <option value="">Select location</option>
                  {locationOptions.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              {viewMode === 'layout' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Floor</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.layout_floor}
                      onChange={(e) => setFormData({ ...formData, layout_floor: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Chair Count</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.chair_count}
                      onChange={(e) => setFormData({ ...formData, chair_count: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      placeholder="Leave empty to match capacity"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Shape</label>
                    <select
                      value={formData.shape}
                      onChange={(e) => setFormData({ ...formData, shape: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    >
                      <option value="rectangle">Rectangle</option>
                      <option value="square">Square</option>
                      <option value="circle">Circle</option>
                      <option value="oval">Oval</option>
                    </select>
                  </div>
                </>
              )}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 transition-all shadow-sm font-medium"
                >
                  {editingTable ? 'Update' : 'Add'} Table
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg hover:bg-gray-200 transition-all border border-gray-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Tables;


